import BaseStore from './BaseStore';
import { observable, action, configure } from 'mobx';
import axios from 'axios';

import config from './Config';

configure({ enforceActions: 'observed' }) // don't allow state modifications outside actions

class ShippingItemModel {
	@observable id = null;
	@observable deliveryNoteId = null;
	@observable deliveryNoteDate = null;
	@observable client = '';
	@observable recipient = '';
	@observable attention = '';
	@observable address = '';
	@observable zip = '';
	@observable city = '';
	@observable provinceIT = '';
	@observable country = '';
	@observable phone = '';
	@observable grossWeight = null;
	@observable description = '';
	@observable packageSeq = null;
	@observable packagesTot = null;
	@observable notes = '';

	static properties = ['deliveryNoteId', 'deliveryNoteDate', 'client', 'recipient', 'attention', 'address', 'zip', 'city', 'provinceIT', 'country'
		,'phone', 'grossWeight', 'description', 'packageSeq', 'packagesTot', 'notes'];

	constructor(props) {
		this.id = props.id || null;
		ShippingItemModel.properties.forEach( key => {
			if (props.hasOwnProperty(key)){
				this[key] = props[key];
			}
		});
	}
}

class ShippingItemListStore extends BaseStore {
	@observable filtered = false;
	@observable printableItems = [];
	@observable allItemsWithoutPagination = [];
	@observable _pagination = { pageSize: 50 };
	@observable _filteredInfo = null;
	@observable _sortedInfo = null;

	@action.bound setPrintableItems(items){
		this.printableItems = items.slice(0);
	}

	@action.bound setPagination(obj) {
		this._pagination = obj;
	}
	@action.bound setFilteredInfo(obj) {
		this._filteredInfo = obj;
	}
	@action.bound setSortedInfo(obj) {
		this._sortedInfo = obj;
	}

	uploadAction = config.apis.shippingItem.uploadFile;

	_fromStateToRESTFilter(opt) {
		let filter = {};
		let pagination = opt.pagination || this._pagination;
		let filteredInfo = opt.filteredInfo || this._filteredInfo;
		if (pagination){
			if (pagination.pageSize){
				filter.limit = pagination.pageSize;
			}
			if (pagination.current && pagination.pageSize){
				filter.skip = pagination.pageSize * (pagination.current - 1);
			}
		}
		if (filteredInfo) {
			let where = {};
			let filterEmpty = true;
			for (let key in filteredInfo){
				if (filteredInfo[key] && filteredInfo[key].length > 0){
					if (key == 'deliveryNoteId'){
						if (filteredInfo[key][0] == -1){
							where[key] = null;
						}
						else {
							where[key] = filteredInfo[key][0];
						}
					}
					else if (key == 'country') {
						const ITALY_REGEXP_STR = '^(it|italy|italia)\\s*$';
						if (filteredInfo[key][0] == '=IT') {
							where[key] = { like: ITALY_REGEXP_STR, options: 'i' };
						}
						else if (filteredInfo[key][0] == '!=IT') {
							where[key] = { nlike: ITALY_REGEXP_STR, options: 'i' };
						}
					}
					else {
						where[key] = { like: `${filteredInfo[key][0]}`, options: 'i' };
					}
					filterEmpty = false;
				}
			}
			if (!filterEmpty){
				filter.where = where;
			}
		}
		return filter;
	}

	loadImplementation(opt, setPaginationInfoCallback) {
		// deliveryNoteId DESC, packageSeq DESC, client ASC, recipient ASC, zip ASC, address ASC, createdAt DESC
		//let filter = { order: 'deliveryNoteId DESC, createdAt DESC, packageSeq DESC' };
		let filter = { order: 'deliveryNoteId DESC, packageSeq DESC, client ASC, city ASC, zip ASC, address ASC, recipient ASC, createdAt DESC' };
		if (opt){
			filter = { ...filter, ...this._fromStateToRESTFilter(opt) };
		}
		let params = {};
		if (filter){
			params.filter = JSON.stringify(filter);
		}
		return axios.get(config.apis.shippingItem.find, {
			...this.auth.getAuthHeaders()
			,params: params
		}).then( (response) => {
			console.log(response.data);
			this.setItems(response.data);
			this.setFiltered(filter && filter.where ? true : false);
			this.count(filter).then( (data) => {
				console.log('Count results', data);
				this.setAllItemsWithoutPagination(data.items);
				if (setPaginationInfoCallback){
					setPaginationInfoCallback(data.count);
				}
			});
			return response.data;
		});
	}

	@action.bound setFiltered(filtered) {
		this.filtered = filtered;
	}

	@action.bound setAllItemsWithoutPagination(items) {
		this.allItemsWithoutPagination = items;
	}

	count(filter) {
		/*if (filter){
			delete filter.limit;
			delete filter.skip;
		}*/
		let params = { filter: {} };
		if (filter) {
			if (filter.order) {
				params.filter.order = filter.order;
			}
			if (filter.where){
				params.filter.where = filter.where;
			}
			params.filter = JSON.stringify(params.filter);
		}
		return axios.get(config.apis.shippingItem.find, {
			...this.auth.getAuthHeaders()
			,params: params
		}).then( (response) => {
			//console.log(response.data);
			return {
				items: response.data
				,count: response.data.length
			};
		});
	}

	fetchById(id) {
		return axios.get(config.apis.shippingItem.findById(id), {
			...this.auth.getAuthHeaders()
		}).then( (response) => {
			console.log(response.data);
			return response.data;
		}).catch( (err) => {
			console.error(err);
			throw err;
		});
	}

	insertImplementation(values){
		return axios.post(config.apis.shippingItem.create, values, {
			...this.auth.getAuthHeaders()
		}).then( (response) => {
			console.log(response.data);
			return response.data;
		}).catch( (err) => {
			if (err.response && err.response.data && err.response.data.error){
				throw err.response.data.error;
			}
			throw err;
		});
	}

	updateImplementation(values){
		//we're patching, not really replacing the attributes.
		//see: https://loopback.io/doc/en/lb3/Exposing-models-over-REST.html
		return axios.patch(config.apis.shippingItem.update(values.id), values, {
			...this.auth.getAuthHeaders()
		}).then( (response) => {
			this.setDirty(); //we have allItemsWithoutPagination that doesn't get patched on updates...
			console.log(response.data);
			return response.data;
		}).catch( (err) => {
			console.error(err);
			throw err;
		});
	}

	deleteImplementation(id) {
		return axios.delete(config.apis.shippingItem.delete(id), {
			...this.auth.getAuthHeaders()
		}).then( (response) => {
			console.log(response.data);
			return response.data;
		}).catch( (err) => {
			console.error(err);
			throw err;
		});
	}

	generateDeliveryNotes(itemIds) {
		return axios.post(config.apis.shippingItem.generateDeliveryNotes, { itemIds: itemIds }, {
			...this.auth.getAuthHeaders()
		}).then( (response) => {
			console.log(response.data);
			return response.data;
		}).catch( (err) => {
			if (err.response && err.response.data && err.response.data.error){
				throw err.response.data.error;
			}
			throw err;
		});
	}

	deleteDeliveryNote(id) {
		return axios.post(config.apis.shippingItem.updateWithWhere, {
			deliveryNoteId: null
			,deliveryNoteDate: null
			,packageSeq: null
			,packagesTot: null 
		}, {
			...this.auth.getAuthHeaders()
			,params: {
				where: { deliveryNoteId: id }
			}
		}).then( (response) => {
			console.log(response.data);
			return response.data;
		}).catch( (err) => {
			if (err.response && err.response.data && err.response.data.error){
				throw err.response.data.error;
			}
			throw err;
		});
	}
}

export { ShippingItemModel, ShippingItemListStore };

